/**
 * Shared date utility functions for analytics date range management.
 * Used across Attendance module screens to enforce max range restrictions.
 */

/** Maximum allowed date range in days (to prevent memory/performance issues) */
export const MAX_ANALYTICS_DATE_RANGE_DAYS = 31;

/** Format a Date object to 'YYYY-MM-DD' string */
export const formatDateStr = (d: Date): string =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

/**
 * Clamp `toDate` so the range from `fromStr` to `toStr` doesn't exceed `maxDays`.
 * Returns `toStr` unchanged if within range, otherwise returns the clamped date string.
 */
export const clampToDate = (
  fromStr: string,
  toStr: string,
  maxDays: number = MAX_ANALYTICS_DATE_RANGE_DAYS
): string => {
  const from = new Date(fromStr);
  const to = new Date(toStr);
  const diffDays = Math.round((to.getTime() - from.getTime()) / 86400000);
  if (diffDays > maxDays) {
    const clamped = new Date(from);
    clamped.setDate(from.getDate() + maxDays);
    return clamped.toISOString().split('T')[0];
  }
  return toStr;
};

/**
 * Get a default date range: `daysBack` days ago → today.
 * Returns `{ from: 'YYYY-MM-DD', to: 'YYYY-MM-DD' }`.
 */
export const getDefaultDateRange = (daysBack: number = 14): { from: string; to: string } => {
  const now = new Date();
  const past = new Date();
  past.setDate(now.getDate() - daysBack);
  return { from: formatDateStr(past), to: formatDateStr(now) };
};
